import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Role } from '../../model/auth/Role';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ProgressService } from '../../service/progress.service';
import { BaseModalComponent } from '../../common/modal/base-modal/base-modal.component';
import { NotificationHandler } from '../../helper/notification.handler';
import { UpdateUserDto, User } from '../../model/auth/User';
import { UserService } from '../../service/user.service';
import { School, SchoolGrade, SchoolType } from '../../model/location/School';
import { Region } from '../../model/location/Region';
import { LocationService } from '../../service/location.service';
import { CustomValidators } from '../../helper/custom.validators';
import { Contest } from '../../model/contest/Contest';
import { environment } from '../../../environments/environment';

export interface UserDetailsData {
  userId: string;
  userRole: Role;

  userSchoolId?: string;
  userSchoolName?: string;

  competitionId?: string;
  contest?: Contest;

  mobileView?: boolean;
}

export interface UserDetailsUpdateResult {
  user: User;
}

@Component({
  selector: 'app-profile-details-modal',
  templateUrl: './profile-details-modal.component.html',
  styleUrls: ['./profile-details-modal.component.scss']
})
export class ProfileDetailsModalComponent extends BaseModalComponent implements OnInit {

  readonly roles = [Role.Admin, Role.Host, Role.Coordinator, Role.Teacher];

  @ViewChild('dialogContent', {static: true}) protected progressContainer!: ElementRef;

  mobileView: boolean;

  selectedGrade?: SchoolGrade;
  selectedRegion?: Region;
  selectedType?: SchoolType;
  selectedSchool?: School;

  allowGradeSelect = false;
  allowRegionSelect = false;
  allowSchoolTypeSelect = false;
  allowSchoolSelect = false;
  showGradeSelect = false;
  showRegionSelect = false;
  showSchoolSelect = false;

  competitionId?: string;
  contest?: Contest;

  emailField = new FormControl('', [
    Validators.email, Validators.required, Validators.minLength(5), Validators.maxLength(128)
  ]);
  emailRequired = true;
  emailChangeAllowed = false;
  emailUnique = false;
  emailCheckInProgress = false;
  phoneField = new FormControl('', [
    CustomValidators.phoneNumber, Validators.minLength(7), Validators.maxLength(15)
  ]);
  showPhoneField = false;
  firstNameField = new FormControl('', [
    Validators.required, Validators.minLength(2), Validators.maxLength(128)
  ]);
  lastNameField = new FormControl('', [
    Validators.required, Validators.minLength(2), Validators.maxLength(128)
  ]);

  profileDetailsForm: FormGroup = new FormGroup({
    email: this.emailField,
    phone: this.phoneField,
    firstName: this.firstNameField,
    lastName: this.lastNameField,
  });

  schoolDataLoading = true;

  private readonly userId: string;
  private readonly userRole: Role;
  user?: User;

  constructor(private userService: UserService,
              private locationService: LocationService,
              dialogRef: MatDialogRef<ProfileDetailsModalComponent>,
              progressService: ProgressService,
              @Inject(MAT_DIALOG_DATA) data: UserDetailsData) {
    super(dialogRef, progressService);

    this.userId = data.userId;
    this.userRole = data.userRole;

    this.competitionId = data.competitionId;
    this.contest = data.contest;

    this.mobileView = data.mobileView == true;
  }

  ngOnInit(): void {
    this.loadUser();

    this.emailField.disable();
    this.showPhoneField = this.userRole != Role.Student;
    if (!this.showPhoneField) this.phoneField.disable();
  }

  private loadUser() {
    this.startProgress();
    this.userService.getUserDetails(this.userId).subscribe({
      next: user => {
        this.user = user;

        this.emailField.setValue(user.email);
        if (this.userRole == Role.Student && (!user.email || (!user.emailVerified && user.status != 'CONFIRMED'))) {
          this.emailChangeAllowed = true;
          if (user.username) {
            this.emailField.setValidators([
              Validators.email, Validators.minLength(5), Validators.maxLength(128)
            ]);
            this.emailRequired = false;
          }
          this.emailField.enable();
          if (this.emailField.value && this.emailField.valid) this.emailUnique = true;
        }

        if (this.contest && this.contest.endsAt && this.contest.endsAt <= new Date()) {
          this.emailField.disable();
        }

        this.phoneField.setValue(user.phone);
        this.firstNameField.setValue(user.firstName);
        this.lastNameField.setValue(user.lastName);

        if (user.region) this.selectedRegion = new Region(user.region, '');
        if (user.schoolType) this.selectedType = new SchoolType(user.schoolType, '');
        this.selectedGrade = user.schoolGrade;

        if (user.schoolId) {
          this.loadSchoolDetails(user.schoolId);
        } else {
          this.schoolDataLoading = false;
          this.stopProgress();
        }
      }, error: error => {
        NotificationHandler.handleError($localize`:Notifications|User details fetch has failed message@@notifications-user-details-fetch-failed-error:User details fetch has failed`, error);

        this.stopProgress();
      }
    });
  }

  searchUserByEmail = () => {
    if (this.emailField.value && this.emailField.valid && !this.emailCheckInProgress) {
      this.emailUnique = false;
      this.emailCheckInProgress = true;
      this.userService.userExistsAuth(<string>this.emailField.value).subscribe({
        next: user => {
          if (!user.id || user.id == this.user?.id) {
            this.emailUnique = true;
          } else {
            this.emailField.setErrors({
              unique: {
                error: $localize`:User Profile|User profile email not unique notification@@user-profile-email-not-unique-notification:This email is already registered in a system`
              }
            });
          }
        }, error: error => {
          console.error('Error while checking user existence', error);
        }, complete: () => {
          this.emailCheckInProgress = false;
        }
      });
    }
  };

  private loadSchoolDetails(schoolId: string) {
    this.locationService.getSchoolDetails(schoolId).subscribe({
      next: school => {
        this.selectedSchool = school;
        this.selectedType = new SchoolType(school.type, '');

        this.schoolDataLoading = false;
        this.stopProgress();
      }, error: error => {
        NotificationHandler.handleError($localize`:Notifications|School details fetch has failed message@@notifications-school-details-fetch-failed-error:School details fetch has failed`, error);
      }, complete: () => {
        this.stopProgress();
      }
    });
  }

  callerRolesUpdated(roles: Role[]) {
    if (roles.includes(Role.Host) || roles.includes(Role.Coordinator)) {
      this.showRegionSelect = true;

      if (roles.includes(Role.Host)) this.allowRegionSelect = true;

      if (this.userRole == Role.Teacher) {
        this.allowSchoolTypeSelect = true;
        this.allowSchoolSelect = true;
        this.showSchoolSelect = true;
      } else if (this.userRole == Role.Student) {
        this.allowGradeSelect = true;
        this.showGradeSelect = true;
        this.allowSchoolTypeSelect = true;
        this.allowSchoolSelect = true;
        this.showSchoolSelect = true;
      }
    } else if (roles.includes(Role.Teacher)) {
      this.allowGradeSelect = true;
      this.showGradeSelect = true;
      this.showSchoolSelect = true;
    }

    if (this.contest && this.contest.endsAt && this.contest.endsAt <= new Date()) {
      this.phoneField.disable();
      this.allowRegionSelect = false;
      this.allowGradeSelect = false;
      this.allowSchoolTypeSelect = false;
      this.allowSchoolSelect = false;
    }
  }

  proceedAllowed(): boolean {
    const studentValid = this.userRole == Role.Student
      && (!!this.selectedGrade && !!this.selectedRegion && !!this.selectedSchool && (this.emailField.disabled || (this.emailField.valid && (this.emailUnique || !this.emailField.value) && !this.emailCheckInProgress)))
      || this.userRole != Role.Student;

    const teacherValid = this.userRole == Role.Teacher && !!this.selectedRegion && !!this.selectedSchool && (this.phoneField.valid || this.phoneField.disabled)
      || this.userRole != Role.Teacher;

    const coordinatorValid = this.userRole == Role.Coordinator && !!this.selectedRegion && (this.phoneField.valid || this.phoneField.disabled)
      || this.userRole != Role.Coordinator;

    return this.profileDetailsForm.valid && studentValid && teacherValid && coordinatorValid;
  }

  proceed(): void {
    this.startProgress();

    const data: UpdateUserDto = {
      propagate: true,
      first_name: this.firstNameField.value as string,
      last_name: this.lastNameField.value as string,

      region: this.selectedRegion ? this.selectedRegion.id : undefined,
      school_id: this.selectedSchool ? this.selectedSchool.id : undefined,
      school_grade: this.selectedGrade || undefined,
    };

    if (this.userRole == Role.Student && this.user && (!this.user.email || !this.user.emailVerified) && this.emailField.value) {
      data.email = this.emailField.value as string;
    }
    if (this.userRole != Role.Student) {
      data.phone = this.phoneField.value as string;
    }

    this.userService.updateUserDetails(this.userId, data, this.competitionId).subscribe({
      next: user => {
        if (this.selectedSchool) {
          user.schoolId = this.selectedSchool.id;
          user.schoolName = this.selectedSchool.short_name || this.selectedSchool.name;
        }
        const result: UserDetailsUpdateResult = {
          user: user
        };

        if (user.poolId && user.poolId != environment.auth.poolId) {
          NotificationHandler.notification($localize`:Notifications|User profile saved without email changes message@@notifications-user-profile-saved-without-email-changes:User profile saved but without email changes. Email cannot be edited for this user`);
        }

        this.closeModal(result);
      }, error: error => {
        NotificationHandler.handleError($localize`:Notifications|User profile update failed message@@notifications-user-profile-update-failed-error:User profile update failed`, error);
        this.stopProgress();
      }
    });
  }

}
